import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { 
  LogOut, Mail, Phone, User, MessageSquare, MapPin, Award, 
  Syringe, Droplet, Zap, Users, BarChart3, TrendingUp, Calendar,
  CheckCircle, XCircle, Clock, QrCode, Settings, Trash2, Image
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import trueLipsIcon from "@/assets/truelips-icon.png";
import { LocationManagement } from "@/components/admin/LocationManagement";
import { MembershipPipeline } from "@/components/admin/MembershipPipeline";
import { EmailSettings } from "@/components/admin/EmailSettings";
import { GHLNotifications } from "@/components/admin/GHLNotifications";
import { PartnerPerformanceKPIs } from "@/components/admin/PartnerPerformanceKPIs";
import { WellnessInterestReport } from "@/components/admin/WellnessInterestReport";
import { ChallengesAnalysisReport } from "@/components/admin/ChallengesAnalysisReport";
import { AutomatedReminders } from "@/components/admin/AutomatedReminders";
import { ProviderPerformanceDashboard } from "@/components/admin/ProviderPerformanceDashboard";
import { ProviderOnboarding } from "@/components/ProviderOnboarding";
import { ProviderUserManagement } from "@/components/admin/ProviderUserManagement";
import { ContentApprovalDashboard } from "@/components/admin/ContentApprovalDashboard";
import { ProviderContentSubmissions } from "@/components/provider/ProviderContentSubmissions";
import { LeadsWithInterests } from "@/components/admin/LeadsWithInterests";

interface ConsultationLead {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  message: string | null;
  created_at: string;
  status?: string;
  contacted_at?: string | null;
  notes?: string | null;
  location_id?: string | null;
  orderly_clicked?: boolean;
  nuda_clicked?: boolean;
}

const AdminPortal = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isProvider, setIsProvider] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [providerLocationName, setProviderLocationName] = useState<string>("");
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate('/admin/login');
        return;
      }

      setSession(session);

      // Check for admin or provider role
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .in('role', ['admin', 'provider']);

      if (error || !roles || roles.length === 0) {
        toast({
          title: "Access Denied",
          description: "You don't have access to this portal.",
          variant: "destructive",
        });
        await supabase.auth.signOut();
        navigate('/admin/login');
        return;
      }

      const userIsAdmin = roles.some(r => r.role === 'admin');
      const userIsProvider = roles.some(r => r.role === 'provider');
      
      setIsAdmin(userIsAdmin);
      setIsProvider(userIsProvider);
      setHasAccess(true);
      setIsLoading(false);

      // Check if provider needs onboarding
      if (userIsProvider && !userIsAdmin) {
        const onboardingKey = `provider_onboarding_${session.user.id}`;
        const hasSeenOnboarding = localStorage.getItem(onboardingKey);
        
        if (!hasSeenOnboarding) {
          // Get provider's location name
          const { data: providerAccess } = await supabase
            .from('provider_access')
            .select('locations(name)')
            .eq('user_id', session.user.id)
            .eq('is_primary', true)
            .maybeSingle();
          
          if (providerAccess?.locations) {
            setProviderLocationName((providerAccess.locations as any).name);
          }
          
          setShowOnboarding(true);
        }
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!session) {
          navigate('/admin/login');
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [navigate, toast]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const handleOnboardingComplete = () => {
    if (session?.user?.id) {
      localStorage.setItem(`provider_onboarding_${session.user.id}`, 'true');
    }
    setShowOnboarding(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const { data: leads = [], isLoading: leadsLoading } = useQuery({
    queryKey: ['consultation-leads'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('consultation_leads')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: hasAccess,
  });

  const { data: locations, isLoading: locationsLoading } = useQuery({
    queryKey: ['admin-locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .order('name', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
    enabled: hasAccess,
  });

  const { data: bookings = [], isLoading: bookingsLoading } = useQuery({
    queryKey: ['admin-bookings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: hasAccess,
  });

  // Fetch memberships for funnel metrics
  const { data: memberships = [] } = useQuery({
    queryKey: ['admin-memberships'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('memberships')
        .select('id, location_id, status, created_at')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: hasAccess,
  });

  // Fetch checkins for funnel metrics
  const { data: checkins = [] } = useQuery({
    queryKey: ['admin-checkins'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('checkins')
        .select('id, location_id, membership_id, created_at')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: hasAccess,
  });

  // Fetch pending content submissions count for badge
  const { data: pendingContentCount = 0 } = useQuery({
    queryKey: ['pending-content-count'],
    queryFn: async () => {
      const [baResult, testResult] = await Promise.all([
        supabase
          .from('provider_before_after_submissions')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase
          .from('provider_testimonial_submissions')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending'),
      ]);
      
      return (baResult.count || 0) + (testResult.count || 0);
    },
    enabled: hasAccess && isAdmin,
  });

  const updateLeadMutation = useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status: string; notes?: string }) => {
      const updateData: any = { 
        status,
        ...(status === 'contacted' && { contacted_at: new Date().toISOString() })
      };
      
      if (notes !== undefined) {
        updateData.notes = notes;
      }

      const { error } = await supabase
        .from('consultation_leads')
        .update(updateData)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultation-leads'] });
      toast({
        title: "Success",
        description: "Lead updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update lead",
        variant: "destructive",
      });
    },
  });

  const deleteLeadMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('consultation_leads')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultation-leads'] });
      toast({
        title: "Success",
        description: "Lead deleted successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete lead",
        variant: "destructive",
      });
    },
  });

  const updateLocationMutation = useMutation({
    mutationFn: async ({ id, field, value }: { id: string; field: string; value: boolean }) => {
      const { error } = await supabase
        .from('locations')
        .update({ [field]: value })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-locations'] });
      toast({
        title: "Success",
        description: "Location updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update location",
        variant: "destructive",
      });
    },
  });

  const totalLeads = leads.length;
  const newLeads = leads.filter(l => l.status === 'new' || !l.status).length;
  const contactedLeads = leads.filter(l => l.status === 'contacted').length;
  const resolvedLeads = leads.filter(l => l.status === 'resolved').length;
  
  const totalLocations = locations?.length || 0;
  const topClinics = locations?.filter(l => l.top_clinic).length || 0;
  
  const treatmentStats = {
    truetox: locations?.filter(l => l.truetox).length || 0,
    truelips: locations?.filter(l => l.truelips).length || 0,
    trueboost: locations?.filter(l => l.trueboost).length || 0,
    truehair: locations?.filter(l => l.truehair).length || 0,
  };

  // Calculate conversion metrics per location
  const checkedInMemberIds = new Set(checkins.map(c => c.membership_id));
  
  const locationConversions = locations?.map(location => {
    const locationLeads = leads.filter(l => l.location_id === location.id);
    const locationMemberships = memberships.filter(m => m.location_id === location.id);
    const locationCheckins = checkins.filter(c => c.location_id === location.id);
    const activeMembers = locationMemberships.filter(m => 
      checkedInMemberIds.has(m.id) && m.status !== 'cancelled'
    ).length;
    
    return {
      locationId: location.id,
      locationName: location.name,
      newLeads: locationLeads.length,
      toxPassCreated: locationMemberships.length,
      activeMembers: activeMembers,
      totalCheckins: locationCheckins.length,
      conversionRate: locationLeads.length > 0 
        ? ((locationMemberships.length / locationLeads.length) * 100).toFixed(1)
        : '0.0'
    };
  }) || [];

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'contacted':
        return <Badge variant="secondary" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />Contacted</Badge>;
      case 'resolved':
        return <Badge className="flex items-center gap-1 bg-green-500"><CheckCircle className="w-3 h-3" />Resolved</Badge>;
      case 'cancelled':
        return <Badge variant="destructive" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Cancelled</Badge>;
      default:
        return <Badge variant="outline" className="flex items-center gap-1"><Clock className="w-3 h-3" />New</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg">Checking permissions...</p>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{isAdmin ? 'Admin Portal' : 'Provider Portal'} | TrueTox Dashboard</title>
        <meta name="description" content={isAdmin 
          ? "Manage TrueTox locations, memberships, leads, and provider content. Admin dashboard for aesthetic clinic network." 
          : "Manage your TrueTox location, check in members, and submit content for approval."
        } />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Provider Onboarding Modal */}
      <ProviderOnboarding 
        isOpen={showOnboarding} 
        onComplete={handleOnboardingComplete}
        locationName={providerLocationName}
      />

      <div className="min-h-screen bg-background">
        {/* Sticky header for mobile */}
        <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-40">
          <div className="container mx-auto px-4 py-3 md:py-4">
            <div className="flex items-center justify-between">
              <h1 className="text-lg md:text-2xl font-bold">{isAdmin ? 'Admin Portal' : 'Provider Portal'}</h1>
              <Button onClick={handleLogout} variant="outline" size="sm" className="h-9">
                <LogOut className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Logout</span>
              </Button>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-4 md:py-8">
          <Tabs defaultValue={isAdmin ? "analytics" : "memberships"} className="space-y-4 md:space-y-6">
            {/* Mobile-optimized scrollable tabs */}
            <TabsList className={`flex w-full overflow-x-auto no-scrollbar ${isAdmin ? 'md:grid md:grid-cols-9' : 'md:grid md:grid-cols-4'} gap-1`}>
              {isAdmin && (
                <TabsTrigger value="analytics" className="flex-shrink-0">
                  <BarChart3 className="w-4 h-4 mr-1 md:mr-2" />
                  <span className="hidden sm:inline">Analytics</span>
                  <span className="sm:hidden">Stats</span>
                </TabsTrigger>
              )}
              {isAdmin && (
                <TabsTrigger value="interests" className="flex-shrink-0">
                  <TrendingUp className="w-4 h-4 mr-1 md:mr-2" />
                  <span className="hidden sm:inline">Interests</span>
                  <span className="sm:hidden">Int</span>
                </TabsTrigger>
              )}
              <TabsTrigger value="memberships" className="flex-shrink-0">
                <QrCode className="w-4 h-4 mr-1 md:mr-2" />
                <span className="hidden sm:inline">Memberships</span>
                <span className="sm:hidden">Members</span>
              </TabsTrigger>
              {/* Reminders tab - hidden for now */}
              {/* <TabsTrigger value="reminders" className="flex-shrink-0">
                <Calendar className="w-4 h-4 mr-1 md:mr-2" />
                <span className="hidden sm:inline">Reminders</span>
                <span className="sm:hidden">Remind</span>
              </TabsTrigger> */}
              <TabsTrigger value="leads" className="flex-shrink-0">
                <MessageSquare className="w-4 h-4 mr-1 md:mr-2" />
                Leads
              </TabsTrigger>
              {/* Content tab - hidden for now */}
              {/* <TabsTrigger value="content" className="flex-shrink-0 relative">
                <Image className="w-4 h-4 mr-1 md:mr-2" />
                Content
                {isAdmin && pendingContentCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs font-bold"
                  >
                    {pendingContentCount > 9 ? '9+' : pendingContentCount}
                  </Badge>
                )}
              </TabsTrigger> */}
              {isAdmin && (
                <>
                  <TabsTrigger value="locations" className="flex-shrink-0">
                    <MapPin className="w-4 h-4 mr-1 md:mr-2" />
                    <span className="hidden sm:inline">Locations</span>
                    <span className="sm:hidden">Locs</span>
                  </TabsTrigger>
                  <TabsTrigger value="provider-profiles" className="flex-shrink-0">
                    <User className="w-4 h-4 mr-1 md:mr-2" />
                    <span className="hidden sm:inline">Users</span>
                    <span className="sm:hidden">Users</span>
                  </TabsTrigger>
                  <TabsTrigger value="settings" className="flex-shrink-0">
                    <Settings className="w-4 h-4 mr-1 md:mr-2" />
                    <span className="hidden sm:inline">Settings</span>
                    <span className="sm:hidden">Set</span>
                  </TabsTrigger>
                </>
              )}
            </TabsList>

            {isAdmin && (
              <TabsContent value="analytics" className="space-y-6">
              {/* Partner Performance KPIs - Nuda vs Orderly */}
              <PartnerPerformanceKPIs />

              {/* Location Conversion Funnel - Full Width */}
              <Card>
                <CardHeader>
                  <CardTitle>Location Conversion Funnel</CardTitle>
                  <CardDescription>Track conversions from lead to member by location</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-[500px] overflow-auto">
                    {locationConversions.map((loc) => (
                      <div key={loc.locationId} className="border-b pb-4 last:border-0">
                        <div className="flex items-start justify-between mb-3">
                          <p className="font-medium text-lg">{loc.locationName}</p>
                          <Badge variant="outline">{loc.conversionRate}% conversion</Badge>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div className="text-center p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{loc.newLeads}</div>
                            <div className="text-xs text-muted-foreground">New Leads</div>
                          </div>
                          <div className="text-center p-3 bg-green-50 dark:bg-green-950 rounded-lg">
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{loc.toxPassCreated}</div>
                            <div className="text-xs text-muted-foreground">Tox Passes Created</div>
                          </div>
                          <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
                            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{loc.activeMembers}</div>
                            <div className="text-xs text-muted-foreground">Active Members</div>
                          </div>
                          <div className="text-center p-3 bg-purple-50 dark:bg-purple-950 rounded-lg">
                            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{loc.totalCheckins}</div>
                            <div className="text-xs text-muted-foreground">Total Check-ins</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Provider Performance Dashboard */}
              <ProviderPerformanceDashboard />

              {/* Treatment Availability - Moved to bottom */}
              <Card>
                <CardHeader>
                  <CardTitle>Treatment Availability</CardTitle>
                  <CardDescription>Number of locations offering each treatment</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Syringe className="w-4 h-4 text-primary" />
                      <span className="font-medium">TrueTox</span>
                    </div>
                    <span className="text-2xl font-bold">{treatmentStats.truetox}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img src={trueLipsIcon} alt="" className="w-4 h-4" />
                      <span className="font-medium">TrueLips</span>
                    </div>
                    <span className="text-2xl font-bold">{treatmentStats.truelips}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-primary" />
                      <span className="font-medium">TrueBoost</span>
                    </div>
                    <span className="text-2xl font-bold">{treatmentStats.trueboost}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-primary" />
                      <span className="font-medium">TrueHair</span>
                    </div>
                    <span className="text-2xl font-bold">{treatmentStats.truehair}</span>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            )}

            {isAdmin && (
            <TabsContent value="interests">
              <LeadsWithInterests />
            </TabsContent>
            )}

            <TabsContent value="memberships">
              <MembershipPipeline />
            </TabsContent>

            <TabsContent value="content">
              {isAdmin ? (
                <ContentApprovalDashboard />
              ) : (
                <ProviderContentSubmissions locationId={sessionStorage.getItem('selectedLocationId') || ''} />
              )}
            </TabsContent>

            <TabsContent value="reminders">
              <Card>
                <CardHeader>
                  <CardTitle>Automated Reminders</CardTitle>
                  <CardDescription>Manage appointment reminders and re-engagement campaigns</CardDescription>
                </CardHeader>
                <CardContent>
                  <AutomatedReminders />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="leads">
              <Card>
                <CardHeader>
                  <CardTitle>Consultation Leads</CardTitle>
                  <CardDescription>
                    Manage and track consultation requests
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {leadsLoading ? (
                    <p className="text-center py-8 text-muted-foreground">Loading leads...</p>
                  ) : leads.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground">No consultation leads yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Phone</TableHead>
                            <TableHead>Location</TableHead>
                            <TableHead>Orderly</TableHead>
                            <TableHead>Nuda</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {leads.map((lead) => (
                            <TableRow key={lead.id}>
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-2">
                                  <User className="h-4 w-4 text-muted-foreground" />
                                  {lead.name}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Mail className="h-4 w-4 text-muted-foreground" />
                                  {lead.email}
                                </div>
                              </TableCell>
                              <TableCell>
                                {lead.phone ? (
                                  <div className="flex items-center gap-2">
                                    <Phone className="h-4 w-4 text-muted-foreground" />
                                    {lead.phone}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {lead.location_id ? (
                                  <div className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm">
                                      {locations?.find(l => l.id === lead.location_id)?.name || 'Unknown'}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {lead.orderly_clicked ? (
                                  <Badge className="bg-green-500 hover:bg-green-600">Yes</Badge>
                                ) : (
                                  <span className="text-muted-foreground">No</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {lead.nuda_clicked ? (
                                  <Badge className="bg-purple-500 hover:bg-purple-600">Yes</Badge>
                                ) : (
                                  <span className="text-muted-foreground">No</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={lead.status || 'new'}
                                  onValueChange={(value) => 
                                    updateLeadMutation.mutate({ id: lead.id, status: value })
                                  }
                                >
                                  <SelectTrigger className="w-32">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="new">New</SelectItem>
                                    <SelectItem value="contacted">Contacted</SelectItem>
                                    <SelectItem value="resolved">Resolved</SelectItem>
                                    <SelectItem value="cancelled">Cancelled</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {formatDate(lead.created_at)}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => {
                                    if (confirm('Are you sure you want to delete this lead?')) {
                                      deleteLeadMutation.mutate(lead.id);
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {isAdmin && (
            <TabsContent value="locations">
              <LocationManagement />
            </TabsContent>
            )}

            {isAdmin && (
            <TabsContent value="provider-profiles">
              <ProviderUserManagement />
            </TabsContent>
            )}

            {isAdmin && (
            <TabsContent value="settings">
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Email & Notification Settings</h2>
                  <p className="text-muted-foreground">Configure admin alerts, journey notifications, and appointment reminders</p>
                </div>
                <EmailSettings />
                
                <div className="pt-6 border-t">
                  <h2 className="text-2xl font-bold mb-2">GHL Integration</h2>
                  <p className="text-muted-foreground mb-6">Monitor and manage GoHighLevel notification triggers and contact syncing</p>
                  <GHLNotifications />
                </div>
              </div>
            </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default AdminPortal;
